import { ModalSize, NotificationInline, WizardModal } from 'components/notifications';
import { EmployeeStep } from './EmployeeStep';
import { ReviewTypeStep } from './ReviewTypeStep';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const defaultArray = [];

// A statement about the modal as a whole rather than about either step, so it leads the modal and
// stands apart from the step's own instruction.
const modalHeader = (
    <NotificationInline
        allowClose={false}
        show
        text="Nothing is recorded until you complete the review."
        intent="primary"
    />
);

// Picks the employee and the review type a review is to be raised for, then opens the
// complete-review wizard keyed on the pair. The picker state lives here so stepping back to the
// employee step returns the manager to the employees and the row they had already chosen.
// Opened from an employee's own profile the employee is already known, so it is supplied as
// employeeId and only the review type is left to pick.
export function NewReviewModal({ isOpen, employeeId = null, onClose }) {
    const navigate = useNavigate();

    const [selectedEmployeeId, setSelectedEmployeeId] = useState(employeeId);
    const [selectedReviewTypeId, setSelectedReviewTypeId] = useState(null);
    const [selectedLocations, setSelectedLocations] = useState(defaultArray);
    const [searchTerm, setSearchTerm] = useState("");
    // Only the modal survives stepping between the two steps, so whether the location selector has
    // a selection of the manager's own to honour is tracked here rather than in the step itself.
    const [locationsInitialised, setLocationsInitialised] = useState(false);

    // A profile's Reviews tab is re-rendered rather than remounted when the route moves to another
    // employee, so the modal follows the employee it is supplied with. The types on offer belong to
    // that employee, so a type picked for the previous one cannot stand.
    useEffect(() => {
        setSelectedEmployeeId(employeeId);
        setSelectedReviewTypeId(null);
    }, [employeeId]);

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        setLocationsInitialised(true);
    }

    function onSelectedEmployeeChange(newEmployeeId) {
        setSelectedEmployeeId(newEmployeeId);

        // The types on offer belong to the chosen employee, so a type picked for another employee
        // cannot stand.
        setSelectedReviewTypeId(null);
    }

    function resetSelection() {
        setSelectedEmployeeId(employeeId);
        setSelectedReviewTypeId(null);
        setSelectedLocations(defaultArray);
        setLocationsInitialised(false);
        setSearchTerm("");
    }

    function onCancel() {
        resetSelection();
        onClose();
    }

    // WizardModal chains off the returned promise to close itself once the wizard is opened.
    function onFinish() {
        navigate(`/employee-reviews/employees/${selectedEmployeeId}/review-types/${selectedReviewTypeId}/complete`);
        resetSelection();
        return Promise.resolve();
    }

    // A known employee leaves nothing to pick on the employee step, and the step's location and
    // employee searches would run for nothing. It has to be left out as a null child rather than
    // hidden, which is also what reduces the wizard to its single remaining step.
    let employeeStep = null;
    // Without the employee step the modal holds a single dropdown, so it is sized to that rather
    // than to the listing the employee step would have brought with it.
    let modalSize = ModalSize.SMALL;

    if (employeeId == null) {
        modalSize = ModalSize.LARGE;
        employeeStep = (
            <EmployeeStep
                selectedEmployeeId={selectedEmployeeId}
                selectedLocations={selectedLocations}
                searchTerm={searchTerm}
                useLocationDefaults={!locationsInitialised}
                onSelectedEmployeeChange={onSelectedEmployeeChange}
                onSelectedLocationsChange={onSelectedLocationsChange}
                onSearchTermChange={setSearchTerm}
            />
        );
    }

    return (
        <WizardModal
            id="new-review-modal"
            isOpen={isOpen}
            title="New Review"
            header={modalHeader}
            size={modalSize}
            canProceed={selectedEmployeeId != null}
            canFinish={selectedReviewTypeId != null}
            finishText="Start Review"
            finishIcon={null}
            onFinish={onFinish}
            onCancel={onCancel}
            resetOnCancel
            // Nothing is recorded until the review itself is completed, so closing this modal
            // discards no work and an unsaved-changes warning would misstate what it does.
            isDirty={false}
        >
            {employeeStep}
            <ReviewTypeStep
                selectedEmployeeId={selectedEmployeeId}
                selectedReviewTypeId={selectedReviewTypeId}
                onSelectedReviewTypeChange={setSelectedReviewTypeId}
            />
        </WizardModal>
    );
}

NewReviewModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onClose: PropTypes.func.isRequired
};
